import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  TextField,
  MuiThemeProvider,
  FormControlLabel,
  Checkbox,
  createTheme,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import {
  deleteUserType,
  getUserTypeById,
  postUserType,
  updateUserType,
} from "../../../../api/userType";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

// Create theme for delete button (red)
const theme = createTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyUserType() {
  return {
    titulo: "",
    historico: [],
    active: true,
    asociado: false,
  };
}

export default function EditUserTypePage() {
  const [userType, setUserType] = useState(getEmptyUserType());
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const userTypeId = useParams().id;
  const history = useHistory();

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    if (!userTypeId) {
      disableLoadingData();
      return;
    }
    getUserTypeById(userTypeId)
      .then((res) => {
        if (res.status === 200) {
          const user = res.data;
          if (
            Array.isArray(res.data.historico) &&
            res.data.historico.length > 0
          ) {
            const latest = res.data.historico.reduce((a, b) =>
              new Date(a.date) > new Date(b.date) ? a : b
            );

            user.precio = latest.price;
          } else {
            user.precio = 0;
          }
          setUserType(user);
          disableLoadingData();
        }
      })
      .catch((error) => {
        alertError({ error: error, customMessage: "Could not get userType." });
        history.push("/usertypes");
      });
  }, [userTypeId, disableLoadingData, history]);

  function saveUserType() {
    let saveUserType = userType;
    if (Array.isArray(userType.historico) && userType.historico.length > 0) {
      // Buscar el registro más reciente
      const latest = userType.historico.reduce((a, b) =>
        new Date(a.date) > new Date(b.date) ? a : b
      );

      // Si el precio actual es diferente al último guardado
      if (userType.precio !== latest.price) {
        saveUserType.historico.push({
          price: userType.precio,
          date: new Date(),
        });
      }
    } else {
      // Si no hay histórico, lo creamos con el precio actual
      saveUserType.historico = [
        {
          price: userType.precio,
          date: new Date(),
        },
      ];
    }
    if (!userTypeId) {
      postUserType(saveUserType)
        .then((res) => {
          if (res.status === 201) {
            alertSuccess({
              title: "Saved!",
              customMessage: "UserType successfully created.",
            });
            history.push("/usertypes");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save userType.",
          });
        });
    } else {
      updateUserType(userTypeId, saveUserType)
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Changes successfully saved.",
            });
            history.push("/usertypes");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save changes.",
          });
        });
    }
  }

  const renderStickyButtons = ({
    onBack,
    onSave,
    onDelete,
    setObj,
    obj,
    showDelete = false,
  }) => {
    return (
      <div
        className="sticky-top bg-white border-top shadow-sm"
        style={{
          zIndex: 1000,
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <div className="container-fluid">
          <div className="row py-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                {/* Botón Back */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onBack}
                    variant="outlined"
                    size="medium"
                    className="text-nowrap"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back
                  </Button>
                </div>

                {/* Botones Save y Delete */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onSave}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "20px" }}
                  >
                    Save
                  </Button>

                  {showDelete && (
                    <MuiThemeProvider theme={theme}>
                      <Button
                        onClick={onDelete}
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        className="text-nowrap"
                      >
                        Delete
                      </Button>
                    </MuiThemeProvider>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleChange = (element) => (event) => {
    if ("semestral" === element || "anual" === element) {
      const updatedPrecio = {
        ...userType.precio,
        [element]: event.target.value,
      };
      setUserType({ ...userType, precio: updatedPrecio });
    } else {
      setUserType({ ...userType, [element]: event.target.value });
    }
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          {renderStickyButtons({
            onBack: () => history.push("/usertypes"),
            onSave: saveUserType,
            onDelete: () => setOpenConfirmDialog(true),
            showDelete: !!userTypeId,
            setObj: setUserType,
            obj: userType,
          })}
          <CardHeader title="Editar tipo de usuario"></CardHeader>
          <CardBody>
            <div className="d-flex">
              <div className="col-md-4">
                <TextField
                  id={`titulo`}
                  label="Titulo"
                  value={userType.titulo}
                  onChange={handleChange("titulo")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`precio`}
                  label="Precio Semestral"
                  value={userType.precio?.semestral}
                  onChange={handleChange("semestral")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  type="number"
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`precio`}
                  label="Precio Anual"
                  value={userType.precio?.anual}
                  onChange={handleChange("anual")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  type="number"
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
            </div>

            {/* Checkboxes */}
            <div style={{ marginTop: "20px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={userType.active || false}
                    onChange={() =>
                      setUserType({ ...userType, active: !userType.active })
                    }
                    name="checkActive"
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={userType.asociado || false}
                    onChange={() =>
                      setUserType({ ...userType, asociado: !userType.asociado })
                    }
                    name="checkAsociado"
                    style={{ marginLeft: "20px" }}
                  />
                }
                label="Asociado"
              />
            </div>
          </CardBody>
        </Card>
        {userTypeId && (
          <>
            <ConfirmDialog
              title={"Are you sure you want to delete this userType?"}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteUserType(userTypeId)
                  .then((res) => {
                    if (res.status === 204 || res.status === 200) {
                      alertSuccess({
                        title: "Deleted!",
                        customMessage: "UserType deleted successfully",
                      });
                      history.push("/usertypes");
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "Could not delete userType.",
                    });
                  });
              }}
            />
          </>
        )}
      </>
    );
}
